import * as THREE from "three";
import { ModelLocation as Location } from "./location";
import * as Extension from "./beacon-loader-extension";
import { Beacon } from "./beacon";
import { Category } from "./category";
import { DistanceModel, getNeededDistanceModel } from "./beacon-distance-model";

export function loadBeaconModel(parameters: {
    model: THREE.Object3D,
    beacon: Beacon,
    category: Category,
    initialLocation: Location,
    defaultImageURL: string,
    modelFailed: (error: string) => void,
    modelLoaded: (model: THREE.Object3D) => void
}) {
    const model = parameters.model;
    const beacon = parameters.beacon;
    const category = parameters.category;
    const defaultImageURL = parameters.defaultImageURL;
    const modelFailed = parameters.modelFailed;
    const modelLoaded = parameters.modelLoaded;

    // const beaconImageA = beacon.beacon_image_a == null ? category.beacon_image_a : beacon.beacon_image_a;
    const badgeImage = beacon.badge_image ?? category.icon;
    const beaconImageA = beacon.beacon_image_a ?? category.beacon_image_a ?? beacon.badge_image ?? category.icon;
    const beaconImageB = beacon.beacon_image_b ?? category.beacon_image_b ?? beacon.badge_image ?? category.icon;
    const beaconImageC = beacon.beacon_image_c ?? category.beacon_image_c ?? beacon.badge_image ?? category.icon;
    // FUTURE: const showDistance = beacon.show_distance == null ? category.show_distance : beacon.show_distance
    const showDistance = category.show_distance;

    const hexColor = Number(`0x${category.color}`);

    let distanceModel: DistanceModel | null = null;
    if (showDistance) {
        distanceModel = getNeededDistanceModel({
            cameraLocation: parameters.initialLocation,
            beaconLocation: { latitude: beacon.latitude, longitude: beacon.longitude },
            useYards: category.use_yards,
            userData: model.userData
        });
    }

    var textureLoadingCount = 0;
    var textureLoadingErrors: string[] = [];

    function textureLoadingCompleted(error?: string) {
        textureLoadingCount--;

        if (error) {
            textureLoadingErrors.push(error);
        }

        if (textureLoadingCount == 0) {
            if (textureLoadingErrors.length > 0) {
                modelFailed(`Could not load textures: ${textureLoadingErrors.join(", ")}`);
            } else {
                modelLoaded(model);
            }
        }
    }

    function findObjectsByName(name: string): THREE.Object3D[] {
        return Extension.findModelObjectsByName(model, name);
    }

    function findObjectsByPrefix(namePrefix: string): THREE.Object3D[] {
        return Extension.findModelObjectsByPrefix(model, namePrefix);
    }

    function findMeshesByName(name: string): THREE.Mesh[] {
        return Extension.findModelMeshesByName(model, name);
    }

    function findMeshesByPrefix(namePrefix: string): THREE.Mesh[] {
        return Extension.findModelMeshesByPrefix(model, namePrefix);
    }

    function meshHierarchy(name: string): THREE.Mesh[] {
        return Extension.modelMeshHierarchy(model, name);
    }

    function loadImage(mesh: THREE.Mesh, image: string, shouldRoundImage: boolean) {
        textureLoadingCount++;

        function loadTexture(texture: THREE.Texture) {
            texture.wrapT = THREE.RepeatWrapping;
            texture.repeat.y = -1;

            if (shouldRoundImage) {
                // Make is circular if it has 4 vertices (a square)
                if (mesh.geometry.attributes.position.count == 4) {
                    mesh.geometry = new THREE.CircleGeometry(
                        (mesh.geometry.boundingBox!.max.x - mesh.geometry.boundingBox!.min.x) / 2,
                        20
                    );
                }
            }

            mesh.material = new THREE.MeshBasicMaterial({
                map: texture,
                side: THREE.DoubleSide,
                transparent: true,
            });

            textureLoadingCompleted();
        }

        new THREE.TextureLoader().load(image, (texture) => {
            // Assumes 1 texture will be loaded on the model
            loadTexture(texture);
        }, () => {
        }, () => {
            console.log(`Could not load beacon image: ${image}`);

            new THREE.TextureLoader().load(defaultImageURL, (texture) => {
                loadTexture(texture);

                console.log(`Loaded beacon deafult image due to error with: ${defaultImageURL}`);
            }, () => {
            }, () => {
                console.log(`Could not load fallback beacon image: ${defaultImageURL}`);

                textureLoadingCompleted(`Could not load fallback beacon image: ${defaultImageURL}`);
            });
        });
    }

    function loadBeaconHead() {
        // Changes all textures in the beaconHeadBorder hierarchy
        meshHierarchy("beaconHeadBorder").forEach(mesh => {
            mesh.material = new THREE.MeshPhysicalMaterial({
                color: hexColor,
                side: THREE.DoubleSide,
                roughness: 0.5
            });
        });

        // A version of the beacon head border to be smoothed by the GPU
        meshHierarchy("beaconHeadRoughBorder").forEach(mesh => {
            mesh.material = new THREE.MeshPhysicalMaterial({
                color: hexColor,
                side: THREE.DoubleSide,
                roughness: 0.5
            });

            if (mesh.geometry) {
                mesh.geometry = Extension.smoothGeometry(mesh.geometry);
            }
        });

        findMeshesByPrefix("beaconFaceBkg").forEach(mesh => {
            mesh.material = new THREE.MeshPhysicalMaterial({ color: 0xFFFFFF });
        });

        findMeshesByPrefix("beaconBadgeImage").forEach(mesh => {
            loadImage(mesh, badgeImage, true);
        });

        findMeshesByPrefix("beaconFaceCircleImage").forEach(mesh => {
            loadImage(mesh, beaconImageA, true);
        });

        findMeshesByPrefix("beaconFaceImageA").forEach(mesh => {
            loadImage(mesh, beaconImageA, false);
        });

        findMeshesByPrefix("beaconFaceImageB").forEach(mesh => {
            loadImage(mesh, beaconImageB, false);
        });

        findMeshesByPrefix("beaconFaceImageC").forEach(mesh => {
            loadImage(mesh, beaconImageC, false);
        });

        findMeshesByPrefix("beaconBillboardBkg").forEach(mesh => {
            mesh.material = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });
        });

        findMeshesByPrefix("beaconBillboardWideImageA").forEach(mesh => {
            loadImage(mesh, beaconImageA, false);
        });

        findMeshesByPrefix("beaconBillboardWideImageB").forEach(mesh => {
            loadImage(mesh, beaconImageB, false);
        });
    }

    function loadBeaconComplication() {
        findObjectsByName("complication").forEach(object => {
            object.removeFromParent();
        });
    }

    function loadBeaconPlacard() {
        // A planar placard border
        findMeshesByPrefix("placardBorder").forEach(mesh => {
            let material = new THREE.MeshPhysicalMaterial();
            material.color.setHex(hexColor);
            material.roughness = 0.5;

            mesh.material = material;
        });

        // A curved placard border
        findMeshesByPrefix("placardSurfaceBorder").forEach(mesh => {
            mesh.material = new THREE.MeshPhysicalMaterial({
                color: 0xFFFFFF,
                opacity: 0.75,
                side: THREE.DoubleSide,
                transparent: true
            });
        });

        findMeshesByPrefix("placardFaceBkg").forEach(mesh => {
            mesh.material = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });
        });

        // Adds a 3D text node to a planar placard
        findMeshesByPrefix("placardText").forEach(mesh => {
            // Truncates text based on length
            let name = beacon.display_name;
            if (name.length > 20) {
                name = name.substring(0, 20) + "…";
            }

            Extension.addTextMesh({ mesh: mesh, name: name, heightPercent: 0.85 });
        });

        findMeshesByPrefix("placardSurfaceTextA").forEach(mesh => {
            // Truncates text based on length
            let name = beacon.display_name;
            if (name.length > 24) {
                name = name.substring(0, 24) + "…";
            }

            Extension.addCurvedTextMesh({ mesh: mesh, name: name });
        });

        findMeshesByPrefix("placardSurfaceTextB").forEach(mesh => {
            if (distanceModel) {
                Extension.addCurvedTextMesh({ mesh: mesh, name: `${distanceModel.distance} ${distanceModel.unitLong}` });
            } else {
                // Truncates text based on length
                let name = beacon.display_name;
                if (name.length > 24) {
                    name = name.substring(0, 24) + "…";
                }

                Extension.addCurvedTextMesh({ mesh: mesh, name: name });
            }
        });
    }

    function loadBeaconPointer() {
        if (showDistance) {
            findObjectsByName("pointerDefault").forEach(object => {
                object.removeFromParent();
            });

            findMeshesByName("pointerComplicationBorder").forEach(mesh => {
                mesh.material = new THREE.MeshPhysicalMaterial({
                    color: hexColor,
                    roughness: 0.5
                });
            });

            findMeshesByName("pointerComplicationBkg").forEach(mesh => {
                mesh.material = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });
            });

            if (distanceModel) {
                const distanceNumberMeshes = findMeshesByName("pointerComplicationDistanceNumber");
                if (distanceNumberMeshes.length > 0) {
                    let distanceNumberMesh = distanceNumberMeshes[0];

                    Extension.addTextMesh({ mesh: distanceNumberMesh, name: `${distanceModel.distance}`, heightPercent: 1 });

                    const distanceUnitMeshes = findMeshesByName("pointerComplicationDistanceUnit");

                    let distanceUnitMesh = distanceUnitMeshes.length > 0 ? distanceUnitMeshes[0] : null;
                    if (distanceUnitMesh) {
                        Extension.addTextMesh({ mesh: distanceUnitMesh, name: distanceModel.unitShort, heightPercent: 0.9 });
                    }
                }
            }
        } else {
            findMeshesByName("pointerDefault").forEach(mesh => {
                mesh.material = new THREE.MeshPhysicalMaterial({
                    color: hexColor,
                    roughness: 0.5
                });
            });

            findObjectsByPrefix("pointerComplication").forEach(object => {
                object.removeFromParent();
            });
        }
    }

    function loadBeaconOpaqueBorder() {
        meshHierarchy("beaconOpaqueBorder").forEach( mesh => {
            mesh.material = new THREE.MeshPhysicalMaterial({
                color: 0xFFFFFF,
                opacity: 0.5,
                transparent: true
            });
        });
        
        findMeshesByName("beaconStem").forEach( mesh => {
            mesh.material = new THREE.MeshPhysicalMaterial({
                color: 0xFFFFFF,
                opacity: 0.5,
                transparent: true
            });
        });
    }

    loadBeaconHead();
    loadBeaconPlacard();
    loadBeaconComplication();
    loadBeaconPointer();
    loadBeaconOpaqueBorder();

    if (textureLoadingCount == 0) {
        // console.log(`TextureLoader had nothing to load: ${beaconName}`)

        modelLoaded(model);
    }
}

// Web AR Only
export function updateBeaconModelLocation(parameters: { model: THREE.Object3D, cameraLocation: Location, beaconLocation: Location, useYards: boolean }) {
    const model = parameters.model;
    const distanceModel = getNeededDistanceModel({
        cameraLocation: parameters.cameraLocation,
        beaconLocation: parameters.beaconLocation,
        useYards: parameters.useYards,
        userData: model.userData
    });

    if (distanceModel) {
        Extension.findModelMeshesByName(model, "pointerComplicationDistanceNumber").forEach(mesh => {
            mesh.children.forEach(child => { child.removeFromParent(); });

            Extension.addTextMesh({ mesh: mesh, name: `${distanceModel.distance}`, heightPercent: 1 });

            Extension.findModelMeshesByName(model, "pointerComplicationDistanceUnit").forEach(mesh => {
                mesh.children.forEach(child => { child.removeFromParent(); });

                Extension.addTextMesh({ mesh: mesh, name: distanceModel.unitShort, heightPercent: 0.9 });
            });
        });

        Extension.findModelMeshesByPrefix(model, "placardSurfaceTextB").forEach(mesh => {
            mesh.children.forEach(child => { child.removeFromParent(); });

            Extension.addCurvedTextMesh({ mesh: mesh, name: `${distanceModel.distance} ${distanceModel.unitLong}` });
        });
    }
}

// Beacon Blender Only
export function updateBeaconModelName(parameters: { model: THREE.Object3D, beaconName: string, showDistance: boolean }) {
    const model = parameters.model;
    const beaconName = parameters.beaconName;
    const showDistance = parameters.showDistance;
    
    // Adds a 3D text node to a planar placard
    Extension.findModelMeshesByPrefix(model, "placardText").forEach(mesh => {
        mesh.children.forEach(child => { child.removeFromParent(); });

        // Truncates text based on length
        let name = beaconName;
        if (name.length > 20) {
            name = name.substring(0, 20) + "…";
        }

        Extension.addTextMesh({ mesh: mesh, name: name, heightPercent: 0.85 });
    });

    Extension.findModelMeshesByPrefix(model, "placardSurfaceTextA").forEach(mesh => {
        mesh.children.forEach(child => { child.removeFromParent(); });

        // Truncates text based on length
        let name = beaconName;
        if (name.length > 24) {
            name = name.substring(0, 24) + "…";
        }

        Extension.addCurvedTextMesh({ mesh: mesh, name: name });
    });

    Extension.findModelMeshesByPrefix(model, "placardSurfaceTextB").forEach(mesh => {
        if (showDistance == false) {
            mesh.children.forEach(child => { child.removeFromParent(); });

            // Truncates text based on length
            let name = beaconName;
            if (name.length > 24) {
                name = name.substring(0, 24) + "…";
            }

            Extension.addCurvedTextMesh({ mesh: mesh, name: name });
        }
    });
}